<script setup>
/**
 * Tickets.
 *
 * A ticket-first lens over the same `conversations` the chat inbox renders -
 * and, since this screen renders the chat inbox's own components, the same
 * screen. `ChatList`, `ConversationBox` and `ConversationSidebar` are the
 * conversations screen; all this view does is turn the ticket lens on and pin
 * the active view's scoping, so a feature added to Conversations arrives here
 * in the same commit rather than having to be ported.
 *
 * Views (All, Unassigned, My open, one per state) stay URL query params, so
 * every view is a link that survives a reload. The open ticket is a path
 * segment, exactly as it is on the chat inbox.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useElementSize } from '@vueuse/core';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConversationDisplayType } from 'dashboard/composables/useConversationDisplayType';
import { useSidepanelToggle } from 'dashboard/composables/useSidepanelToggle';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useTableScrollMemory } from 'dashboard/composables/useTableScrollMemory';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { PANEL_RAIL_MIN_WIDTH } from 'dashboard/constants/breakpoints';
import wootConstants from 'dashboard/constants/globals';

import ChatList from 'dashboard/components/ChatList.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import ConversationTable from 'dashboard/components/widgets/conversation/ConversationTable.vue';
import ConversationTableToolbar from 'dashboard/components/widgets/conversation/ConversationTableToolbar.vue';
import ConversationTableEmptyState from 'dashboard/components/widgets/conversation/ConversationTableEmptyState.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import LayoutSwitcherPill from 'dashboard/components-next/Conversation/LayoutSwitcherPill.vue';
import SidepanelRail from 'dashboard/components-next/Conversation/SidepanelRail.vue';
import CreateTicketDialog from './components/CreateTicketDialog.vue';
import {
  presentTicket,
  rowMatchesSearch,
} from 'dashboard/helper/conversationRowPresenter';
import {
  ASSIGNEE_TYPE,
  CONVERSATION_STATUS,
  DEFAULT_VIEW_KEY,
  findView,
  isTicketConversation,
} from 'dashboard/helper/conversationLens';

const props = defineProps({
  conversationId: { type: [String, Number], default: 0 },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const { isEnterprise } = useConfig();
const { uiSettings, updateUISettings } = useUISettings();
const { conversationDisplayType, isOnExpandedLayout } =
  useConversationDisplayType();
const { getPlainText } = useMessageFormatter();
useSidepanelToggle();

const ticketsViewRef = ref(null);
const { width: viewWidth } = useElementSize(ticketsViewRef);
const createTicketDialogRef = ref(null);

const chatList = useMapGetter('getAllConversations');
const currentChat = useMapGetter('getSelectedChat');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const ticketViews = useMapGetter('customViews/getTicketViews');

// ---------------------- The active view ----------------------

const activeView = computed(() => findView(route.query.view));
const customViewId = computed(() => route.query.custom_view);
const activeCustomView = computed(() =>
  ticketViews.value.find(view => String(view.id) === String(customViewId.value))
);

const headerTitle = computed(
  () => activeCustomView.value?.name ?? t(activeView.value.labelKey)
);

// A saved view carries its own filter payload, so the view's status/assignee
// scoping would fight it. ChatList reads the saved view through `foldersId`
// instead, exactly as the conversations screen does with a folder.
//
// The three primary views open on a scope the agent can then narrow with the
// tabs and the status filter. A state view IS its status, so it takes both
// controls away rather than offering to leave the view without saying so.
const listScope = computed(() => {
  if (customViewId.value) {
    return {
      assigneeType: '',
      status: '',
      resolution: undefined,
      pinned: false,
    };
  }
  const { assigneeType, status, resolution, primary } = activeView.value;
  // A view that names its own assignee or status owns that scope: leaving the
  // assignee tabs and status filter live would let the list contradict the
  // sidebar entry and the URL that claim it. Only `All`, which claims no scope,
  // keeps them. Mirrors the Chats queues (ConversationView `hide-scope-controls`).
  const scoped =
    assigneeType !== ASSIGNEE_TYPE.ALL || status !== CONVERSATION_STATUS.ALL;
  return { assigneeType, status, resolution, pinned: !primary || scoped };
});

// ---------------------- Layout ----------------------

// Tickets shares `conversation_display_type` with the chat inbox rather than
// keeping a layout of its own, so the toggle behaves the same on both screens
// and the preference follows the user instead of the URL. The open ticket
// keeps the layout on - it replaces the table rather than dropping back to the
// columns - so both routes count as the table.
const isOnTableLayout = computed(
  () =>
    ['tickets_index', 'tickets_conversation'].includes(route.name) &&
    conversationDisplayType.value === wootConstants.LAYOUT_TYPES.TABLE
);

// The open ticket replaces the table rather than floating above it, so the two
// are never on screen together.
const isTableVisible = computed(
  () => isOnTableLayout.value && !props.conversationId
);

const tableScrollRef = useTableScrollMemory(isTableVisible);

const showConversationList = computed(() =>
  isOnExpandedLayout.value ? !props.conversationId : true
);

const showMessageView = computed(() =>
  props.conversationId ? true : !isOnExpandedLayout.value
);

// The list request already carries the ticket stamp, so what comes back is
// tickets only. The narrowing is belt and braces over a store shared with the
// chat inbox.
const tableRows = computed(() =>
  chatList.value
    .filter(isTicketConversation)
    .map(conversation => presentTicket(conversation, getPlainText))
);

// The search narrows the rows the active view already loaded; the filters,
// which the server answers, stay the toolbar's business.
const tableSearch = ref('');
const visibleRows = computed(() =>
  tableRows.value.filter(row => rowMatchesSearch(row, tableSearch.value))
);

// ---------------------- Side panels ----------------------

const shouldShowSidebar = computed(
  () =>
    Boolean(currentChat.value.id) && uiSettings.value.is_contact_sidebar_open
);

// Once the Details panel has been opened it stays mounted and toggles with
// v-show, so switching tabs never remounts it (no flicker, no refetch).
const detailsPanelWasOpened = ref(false);
watch(
  shouldShowSidebar,
  isOpen => {
    if (isOpen) detailsPanelWasOpened.value = true;
  },
  { immediate: true }
);

// Which sidepanel is open, if any. Both flags are false once the panel is
// collapsed, which is exactly when the rail stands in for it.
const openPanelKey = computed(() => {
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_copilot_panel_open: isCopilotPanelOpen,
  } = uiSettings.value;
  if (isCopilotPanelOpen) return 'copilot';
  if (isContactSidebarOpen) return 'details';
  return null;
});

// The panel the rail reopens: the one that was last open, not always Details.
const lastOpenPanelKey = ref('details');

const showPanelRail = computed(
  () =>
    !openPanelKey.value &&
    Boolean(currentChat.value.id) &&
    viewWidth.value >= PANEL_RAIL_MIN_WIDTH
);

// Mirrors SidepanelTabs: without Copilot the icon would be a dead end.
const panelRailTabs = computed(() => {
  const tabs = [
    {
      key: 'details',
      icon: 'i-lucide-user-round',
      label: t('CONVERSATION.SIDEBAR.DETAILS'),
    },
  ];
  if (
    isEnterprise &&
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN)
  ) {
    tabs.push({
      key: 'copilot',
      icon: 'i-lucide-sparkles',
      label: t('CONVERSATION.SIDEBAR.COPILOT'),
    });
  }
  return tabs;
});

// The rail drives the same UI settings the header button and the panel's own
// tabs write, so the panel and the rail can never disagree.
const openPanel = key => {
  updateUISettings({
    is_contact_sidebar_open: key === 'details',
    is_copilot_panel_open: key === 'copilot',
  });
};

// ---------------------- Selection ----------------------

const findTicket = () =>
  chatList.value.find(chat => chat.id === Number(props.conversationId));

const setActiveChat = () => {
  if (!props.conversationId) {
    store.dispatch('clearSelectedState');
    return;
  }
  const selected = findTicket();
  if (!selected || selected.id === currentChat.value.id) return;
  const { messageId } = route.query;
  store
    .dispatch('setActiveChat', { data: selected, after: messageId })
    .then(() => emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId }));
};

// A deep-linked ticket is not in the list the active view fetched, so it is
// fetched on its own and lands in the same store the list reads.
const fetchTicketIfUnavailable = () => {
  if (!props.conversationId) return;
  if (!findTicket()) {
    store.dispatch('getConversation', props.conversationId);
  }
};

// The row - and a freshly created ticket, which the active view has not
// fetched either - opens the ticket full screen in place of the table. The
// layout preference is untouched, so the back arrow lands on the table again.
const openTicket = id =>
  router.push({
    name: 'tickets_conversation',
    params: { conversationId: id },
    query: route.query,
  });

// The table it was opened from is where leaving the ticket lands - the same
// place the header's back arrow points at.
const returnToTable = () =>
  router.push({ name: 'tickets_index', query: route.query });

useKeyboardEvents({
  Escape: () => {
    if (isOnTableLayout.value && props.conversationId) returnToTable();
  },
});

const markSolved = ids =>
  Promise.all(
    ids.map(conversationId =>
      store.dispatch('toggleStatus', {
        conversationId,
        status: wootConstants.STATUS_TYPE.RESOLVED,
      })
    )
  );

watch(
  () => props.conversationId,
  () => {
    fetchTicketIfUnavailable();
    setActiveChat();
  }
);
watch(() => chatList.value.length, setActiveChat);
watch(
  openPanelKey,
  key => {
    if (key) lastOpenPanelKey.value = key;
  },
  { immediate: true }
);

onMounted(() => {
  // `findView` already falls back to the default, but the sidebar matches its
  // active entry on the query - without the param no view reads as active.
  if (!route.query.view && !route.query.custom_view) {
    router.replace({ query: { ...route.query, view: DEFAULT_VIEW_KEY } });
  }
  store.dispatch('agents/get');
  store.dispatch('portals/index');
  store.dispatch('customViews/get', 'conversation');
  fetchTicketIfUnavailable();
  setActiveChat();
});
</script>

<template>
  <section
    ref="ticketsViewRef"
    class="flex w-full h-full min-w-0 bg-n-background lg:gap-2 lg:p-2"
  >
    <!-- ChatList owns the list request, so it stays mounted (and hidden) under
         the table layout too - the table reads the same store it fills. -->
    <ChatList
      tickets
      :title="headerTitle"
      :assignee-type-override="listScope.assigneeType"
      :status-override="listScope.status"
      :resolution="listScope.resolution"
      :hide-scope-controls="listScope.pinned"
      :folders-id="customViewId || 0"
      :show-conversation-list="!isOnTableLayout && showConversationList"
      :is-on-expanded-layout="isOnExpandedLayout"
    />

    <!-- The table stays mounted under the open ticket, so returning to it keeps
         the selection and scroll offset it was left with. -->
    <div
      v-if="isOnTableLayout"
      v-show="isTableVisible"
      ref="tableScrollRef"
      class="flex-1 min-w-0 min-h-0 p-3 overflow-y-auto md:p-4"
    >
      <ConversationTableToolbar
        v-model:search="tableSearch"
        :title="headerTitle"
        show-create
        :create-label="t('TICKETS.CREATE.TITLE')"
        @create="createTicketDialogRef.open()"
      />
      <ConversationTable
        v-if="visibleRows.length"
        :tickets="visibleRows"
        @open="openTicket"
        @mark-solved="markSolved"
      />
      <ConversationTableEmptyState
        v-else
        copy-key="TICKETS.TABLE"
        :show-clear-search="Boolean(tableSearch)"
        @clear-search="tableSearch = ''"
      />
    </div>
    <!-- The ticket is the screen once it is open: on the table layout it
         replaces the table, everywhere else it sits beside the list. -->
    <template v-if="!isTableVisible">
      <ConversationBox
        v-if="showMessageView"
        class="lg:overflow-hidden lg:rounded-xl lg:border lg:border-n-weak lg:shadow-sm [&_.conversation-panel]:bg-n-background [&_.reply-box]:!mx-3 [&_.reply-box]:!mb-3 [&_.reply-box]:!border-n-weak [&_.reply-box]:shadow-lg [&_.reply-box]:shadow-n-slate-12/5"
        :is-on-expanded-layout="isOnExpandedLayout"
        :show-back-button="isOnTableLayout"
      />
      <ConversationSidebar
        v-if="currentChat.id && detailsPanelWasOpened"
        v-show="shouldShowSidebar"
        class="motion-safe:animate-panel-slide-in"
        :current-chat="currentChat"
      />
      <SidepanelRail
        v-if="showPanelRail"
        :tabs="panelRailTabs"
        :active-key="lastOpenPanelKey"
        test-id="ticket-panel-rail"
        class="flex-shrink-0 w-[3.25rem] border rounded-xl border-n-weak bg-n-surface-1 shadow-sm"
        @select="openPanel"
      />
    </template>
    <CreateTicketDialog ref="createTicketDialogRef" @created="openTicket" />
    <CmdBarConversationSnooze />
    <LayoutSwitcherPill v-if="!conversationId" />
  </section>
</template>
